import { useMemo, Fragment } from "react";
import { Link, useParams } from "react-router-dom";

import { Chip } from "ui/chip";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentUser } from "hooks/api/use-current-user";
import { useBatch } from "hooks/api/use-batches";
import { useTouchpointVersions } from "hooks/api/use-touchpoint-versions";
import { useRetrySyncTouchpoint } from "hooks/api/use-touchpoints";

import { getBatchPath } from "utilities/app-routes";
import { getSyncBadgeConfig } from "utilities/sync-status";
import { hasPermission, hasRole } from "utilities/user";
import { formatFiscalYear, toSpacedTitleCase } from "utilities";
import { getTouchpointClassChipColor } from "utilities/touchpoint-class-chip";

import { useFeatureFlag } from "contexts/feature-flag-context";

import { Permission, Role } from "types/auth";
import { TouchpointStatuses } from "types";

import Icons from "assets";
import { Button } from "ui/button";
import { TouchpointDTOBatchJobExecutionStatus, TouchpointDTOBatchJobType } from "generated/schemas";

const TouchpointPageHeaderMetadata = ({
  touchpointBatchId,
  draftCount,
  isProgramTemplate = false,
  selectedVersionFiscalYear,
}: {
  touchpointBatchId?: string;
  draftCount: number;
  isProgramTemplate?: boolean;
  selectedVersionFiscalYear?: number;
}) => {
  const currentUser = useCurrentUser();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const params = useParams();
  const isBatchManagementEnabled = useFeatureFlag("batch-management");
  const retrySyncMutation = useRetrySyncTouchpoint();
  const isClientUser = hasPermission(currentUser, Permission.PERM_CLIENT_USER);
  const clientId = params?.clientId ?? currentUser.client?.id;
  const { data: versions } = useTouchpointVersions({
    clientId,
    campaignId: currentCampaign.id,
    touchpointId: currentTouchpoint.id,
  });

  const syncStatus = currentTouchpoint.batchJobExecutionStatus;
  const syncBadge = getSyncBadgeConfig(syncStatus, currentTouchpoint.batchJobType);
  const isWritingJob = syncBadge?.jobType === TouchpointDTOBatchJobType.WRITING;
  const SyncBadgeIcon = isWritingJob ? Icons.CampaignWriter : Icons.MondayBoard;
  const mondayBoardUrl = currentTouchpoint.mondayBoardLink;
  const touchpointClasses = currentTouchpoint.classes ?? [];
  const { data: currentBatch } = useBatch(
    {
      clientId,
      batchId: touchpointBatchId,
    },
    {
      enabled: isBatchManagementEnabled && !!touchpointBatchId,
    },
  );

  const getLinkToBatch = () => {
    if (!touchpointBatchId || !isBatchManagementEnabled) {
      return;
    }

    return getBatchPath({
      clientId: params?.clientId,
      batchId: touchpointBatchId,
      isClientUser: !hasRole(currentUser, Role.ROLE_SUPER_ADMIN),
    });
  };

  const publishedVersion = useMemo(
    () => versions?.items.find((v) => v.status === TouchpointStatuses.PUBLISHED)?.version,
    [versions?.items],
  );

  const showDrafts = draftCount > 0 && !isClientUser;

  const linkToBatch = getLinkToBatch();

  const batchName = currentBatch?.name;
  const hideBatchBadgeForClientPortal = isClientUser && currentBatch?.status === "APPROVED";
  const showBatchBadge =
    isBatchManagementEnabled && linkToBatch && batchName && !hideBatchBadgeForClientPortal;

  const metadataItems = [
    {
      id: "population-and-class-badges",
      element: (currentTouchpoint.populationType || touchpointClasses.length > 0) && (
        <div className="flex items-center gap-1 flex-wrap">
          {currentTouchpoint.populationType && (
            <Chip
              size="small"
              variant="outlined"
              label={toSpacedTitleCase(currentTouchpoint.populationType)}
            />
          )}
          {touchpointClasses.map((classType) => (
            <Chip
              key={classType}
              size="small"
              variant="outlined"
              color={getTouchpointClassChipColor(classType)}
              label={toSpacedTitleCase(classType)}
            />
          ))}
        </div>
      ),
    },
    {
      id: "published-version",
      element: !isProgramTemplate && !!versions?.items.length && (
        <span>{publishedVersion ? `v${publishedVersion}` : "No published version"}</span>
      ),
    },
    {
      id: "draft-count",
      element: showDrafts && (
        <span>
          {draftCount} {draftCount === 1 ? "draft" : "drafts"}
        </span>
      ),
    },
    {
      id: "fiscal-year",
      element: !isClientUser && !isProgramTemplate && selectedVersionFiscalYear && (
        <span>{formatFiscalYear(selectedVersionFiscalYear)}</span>
      ),
    },
    {
      id: "batch-link",
      element: showBatchBadge && (
        <Link to={linkToBatch} className="flex items-center gap-1 text-aqua-600 cursor-pointer">
          <Icons.Layers className="size-4" />
          <div className="text-center">
            <span>Batch:</span>
            <span className="font-semibold ml-1">{batchName}</span>
          </div>
        </Link>
      ),
    },
    {
      id: "sync-status",
      element:
        !isClientUser && syncBadge ? (
          <>
            <Chip
              variant="outlined"
              color={syncBadge.color}
              size="small"
              label={syncBadge.label}
              icon={<SyncBadgeIcon />}
            />
            {!isWritingJob &&
              syncStatus === TouchpointDTOBatchJobExecutionStatus.FAILED && (
                /* The explicit 12px glyph pins what this button rendered at the pre-rename `xs` size, whose
                   `ICON_PX` step was 12 where `small`'s is 16. It is the one call site the size rename
                   would otherwise have changed — see §9 in `docs/deferred-work.md`. */
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Icons.Replay width={12} height={12} />}
                  onClick={() => retrySyncMutation.mutate(currentTouchpoint.id)}
                  disabled={retrySyncMutation.isPending}
                >
                  Retry
                </Button>
              )}
          </>
        ) : null,
    },
    {
      id: "monday-board-link",
      element: !isClientUser && !!mondayBoardUrl && (
        <Button
          variant="text"
          endIcon={<Icons.Forward className="!size-3" />}
          onClick={() => window.open(mondayBoardUrl, "_blank", "noopener,noreferrer")}
        >
          View on monday.com
        </Button>
      ),
    },
  ].filter((item) => item.element);

  return (
    <div className="flex items-center justify-start text-sm gap-3 pb-2">
      {metadataItems.map((item, index) => (
        <Fragment key={item.id}>
          {index > 0 && <span className="bg-gray-400 h-5 w-px" />}
          {item.element}
        </Fragment>
      ))}
    </div>
  );
};

export default TouchpointPageHeaderMetadata;
